// @i18n-ignore-file
import { useStatsigClient } from '@statsig/react-bindings'

import {
  FILTER_VERSION_CONFIG,
  FilterVersionConfig,
} from '@src/pages/[...slug]/components/collection/filter-version-config'
import { MODULES, FEATURES } from '@src/quince-site-common/src/constants/template-info'
import { captureException } from '@src/quince-site-common/src/utils/sentryUtil'

const COLLECTION_FILTERS_CONFIG_NAME = 'collection-filters-config'

/**
 * Client-side hook that mirrors the server-side `getCollectionFiltersConfig`
 * helper. Reads the Statsig dynamic config `collection-filters-config` and
 * falls back to the locally-defined FILTER_VERSION_CONFIG when the config is
 * missing, empty, or Statsig is unavailable.
 *
 * The returned shape drives `SelectedFilter.getSelectedFilterFromQueryParamsWithV2Support`
 * for client-side filter parsing — keep the SSR fetch and this hook reading the
 * same config so PL/BFF and the client agree on which filter format wins.
 */
export const useStatsigCollectionFiltersConfig = (): FilterVersionConfig => {
  const { getDynamicConfig } = useStatsigClient()
  try {
    const config = getDynamicConfig(COLLECTION_FILTERS_CONFIG_NAME)
    const value = config.value as Partial<FilterVersionConfig> | undefined
    // Fall back to the local default whenever the Statsig payload is missing
    // or doesn't carry the fields we need — matches the server helper's
    // default-on-failure contract so the two paths agree.
    if (!value?.enabled || !value.versionPriority || !value.queryUpdateVersion) {
      return FILTER_VERSION_CONFIG
    }

    return value as FilterVersionConfig
  } catch (error) {
    captureException(error as Error, {
      module: MODULES.COLLECTION,
      feature: FEATURES.CLP_FILTERS_CONFIG, // or a new CLP_FILTERS_CONFIG entry
      additionalTags: { source: 'useStatsigCollectionFiltersConfig' },
      extraInfo: { configKey: COLLECTION_FILTERS_CONFIG_NAME },
    })
    return FILTER_VERSION_CONFIG
  }
}
